import {_} from 'lodash';
import * as ko from "knockout";
import {IdName, IMessageData, IObjectType} from "../../index";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {AvailableLanguages, ILanguage, REQUEST_MESSAGE} from "../../constants/Language";
import PromiseRequest from "../../req/PromiseRequest";
import {global} from "../../appContext";
import {ObjectTypeId, ObjectTypeId2ImportFormatId} from "../../constants";
import {storeObservable, storeObservableArray} from "../../util/sessionstore";
import {createErrorMessage} from "../../util/misc";
import {ImportFormatId} from "../../constants/ImportFormats";
import contextHtml from './context.html?raw';

export const template = contextHtml;

export class viewModel {
    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly req_getimportformats: PromiseRequest = global.getPromiseRequest('getimportformats')

    private objectTypes: IObjectType[];
    private languages: ILanguage[] = AvailableLanguages;
    private importFormats!: IdName[];

    private readonly selectedObjectTypeId: KnockoutObservable<ObjectTypeId> = storeObservable('syncfields.config.objecttypeid')
    private readonly selectedLanguageId: KnockoutObservableArray<ILanguage['id']> = storeObservableArray('syncfields.config.language')
    private readonly selectedImportFormatId: KnockoutObservable<ILanguage['id']> = storeObservable('syncfields.config.importformat', ImportFormatId.Visma)
    private readonly selectedOrganization = global.login.selectedOrganization

    constructor(params: { objectTypes: IObjectType[] }) {
        this.objectTypes = params.objectTypes
        this.kosub.subscribe(this.selectedObjectTypeId, this.configureForm.bind(this))
        this.selectedImportFormatId(this.defaultImportFormat().id)
        this.configureForm()
        this.init();
    }

    private init(): Promise<any> {
        this.isReady(false);
        this.messageData({message: REQUEST_MESSAGE});

        return Promise.resolve()
            .then(() => {
                return this.req_getimportformats.request()
                    .then((data: IdName[]) => this.importFormats = data)
            })
            .then(() => {
                this.messageData(null)
                this.isReady(true)
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)))
            })
    }

    private configureForm() {
        const importformatid = this.getDefaultImportFormatId(this.selectedObjectTypeId())
        if (importformatid != null) {
            this.selectedImportFormatId(importformatid)
            const format = this.importFormats?.find(o => o.id == this.selectedImportFormatId())
            if (format) {
                global.snackbarData({message: `((changed)) ((importformat)) [${format.name}]`})
            }
        }
    }

    private readonly enableImportFormat: KnockoutComputed<boolean> = ko.pureComputed((): boolean => {
        // unless there is a default import format it should be mutable
        return !this.getDefaultImportFormatId(this.selectedObjectTypeId())
    })

    private getDefaultImportFormatId(objectTypeId: ObjectTypeId): ImportFormatId | null {
        if (!objectTypeId) {
            return null
        }
        return ObjectTypeId2ImportFormatId[objectTypeId] ?? null
    }

    private isDefaultImportFormatSelected = ko.pureComputed((): boolean => {
        return this.selectedImportFormatId() == this.defaultImportFormat().id
    })

    private onDefaultImportFormatClick() {
        this.selectedImportFormatId(this.defaultImportFormat().id)
    }

    private defaultImportFormat = ko.pureComputed((): IdName => {
        return this.selectedOrganization()?.importformat ?? {id: ImportFormatId.Visma, name: ''}
    })

    private isAllChecked(): boolean {
        const allLanguageIds = this.languages.map(o => o.id).sort()
        const selectedLanguageIds = this.selectedLanguageId().sort()
        return _.isEqual(allLanguageIds, selectedLanguageIds)
    }
    private onAllClicked() {
        const willBeChecked = !this.isAllChecked()
        if (willBeChecked) {
            const allLanguageIds = this.languages.map(o => o.id).sort()
            this.selectedLanguageId(allLanguageIds)
        } else {
            this.selectedLanguageId([])
        }
        return true
    }
    private isSomeChecked(): boolean {
        if (!this.selectedLanguageId().length) {
            return false
        }
        const allLanguageIds = this.languages.map(o => o.id).sort()
        const selectedLanguageIds = this.selectedLanguageId().sort()
        return !_.isEqual(allLanguageIds, selectedLanguageIds)
    }

    dispose() {
        this.kosub.unsuscribeAll();
        this.req_getimportformats.abort()
    }
}
